import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { NotificationView } from '@app/shared';
import { Icon, Icons, IconButton, Menu, MenuItem, Text } from '../ui';
import { api } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { notificationTarget } from './appShellNav';

/**
 * The notifications bell for the console top bar. Reuses the existing notifications
 * queries (unread badge + recent list), mark-read mutation, and `notificationTarget`
 * routing — re-presented in the Chakra shell via the design-system {@link Menu}. Test
 * ids are preserved (`notification-badge`, `notification-menu`, `notification-item`,
 * `notification-empty`) so the scheduled-run notification E2E keeps working.
 */
export function NotificationsBell({ workspaceId }: { workspaceId: string | null }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const unreadQuery = useQuery({
    queryKey: qk.notifications(workspaceId ?? ''),
    queryFn: () => api.listNotifications(workspaceId as string, { unread: true, pageSize: 50 }),
    enabled: !!workspaceId,
  });
  const unread = unreadQuery.data?.data.length ?? 0;

  const recent = useQuery({
    queryKey: [...qk.notifications(workspaceId ?? ''), 'recent'],
    queryFn: () => api.listNotifications(workspaceId as string, { pageSize: 15 }),
    enabled: open && !!workspaceId,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(workspaceId as string, id),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: qk.notifications(workspaceId ?? '') }),
  });

  const openNotification = (n: NotificationView) => {
    const target = notificationTarget(n);
    setOpen(false);
    if (!n.readAt) markRead.mutate(n.id);
    void navigate({ to: target.to, params: target.params });
  };

  const items = recent.data?.data ?? [];

  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      label="Notifications"
      width="22rem"
      data-testid="notification-menu"
      trigger={
        <IconButton
          aria-label="notifications"
          data-testid="notification-badge"
          variant="ghost"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          icon={<BellWithCount count={unread} />}
        />
      }
    >
      {items.length === 0 ? (
        <MenuItem disabled data-testid="notification-empty">
          <Text tone="muted">No notifications</Text>
        </MenuItem>
      ) : (
        items.map((n) => {
          const target = notificationTarget(n);
          return (
            <MenuItem
              key={n.id}
              data-testid="notification-item"
              data-unread={!n.readAt ? 'true' : undefined}
              onClick={() => openNotification(n)}
            >
              <Text weight="medium">{target.primary}</Text>
              <Text tone="muted" size="sm">
                {target.secondary}
              </Text>
            </MenuItem>
          );
        })
      )}
    </Menu>
  );
}

function BellWithCount({ count }: { count: number }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <Icon as={Icons.Bell} size="md" tone="current" />
      {count > 0 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            minWidth: '16px',
            height: '16px',
            padding: '0 4px',
            borderRadius: '9999px',
            background: 'var(--ehe-colors-status-warning)',
            color: 'var(--ehe-colors-text-inverse)',
            fontSize: '10px',
            fontWeight: 700,
            lineHeight: '16px',
            textAlign: 'center',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  );
}
